import {
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { AppReviewRepository } from "../repositories/AppReviewRepository";
import { useDispatch } from "react-redux";
import {
  notifyShowErrorMessage,
  notifyShowSuccessMessage,
} from "../../store/AlertSlice";
import PdfViewer from "../entries/PdfViewer";
import ConfirmationDialog from "../common/ConfirmationDialog";
import {
  Add,
  AddCircle,
  ArrowDownwardOutlined,
  ArrowUpwardOutlined,
  DeleteOutline,
  RemoveCircle,
} from "@mui/icons-material";
import { Stack } from "@mui/system";

export const GenerateReviewDecision = ({
  open,
  setOpen,
  appReview,
  setAppReview,
}) => {
  const [saving, setSaving] = useState(false);
  const [mvrMinister, setMvrMinister] = useState("");
  const [jpdpDirector, setJpdpDirector] = useState("");
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState();

  const [subject, setSubject] = useState();
  const [caseDocuments, setCaseDocuments] = useState("");
  const [decision, setDecision] = useState("");
  const [summary, setSummary] = useState("");
  const [remarks, setRemarks] = useState("");

  const dispatch = useDispatch();

  useEffect(() => {
    const handler = (event) => {
      if (event.source !== window) return;

      const msg = event.data;
      if (msg && typeof msg.data === "string") {
        const base64 = msg.data
          .replace(/-/g, "+")
          .replace(/_/g, "/")
          .replace(/\s+/g, "");

        try {
          const decodedJsonString = atob(base64);
          const parsed = JSON.parse(decodedJsonString);
          const pdfBase64 = parsed.message;

          const byteCharacters = atob(pdfBase64);
          const byteNumbers = new Array(byteCharacters.length)
            .fill(0)
            .map((_, i) => byteCharacters.charCodeAt(i));
          const byteArray = new Uint8Array(byteNumbers);
          const pdfBlob = new Blob([byteArray], {
            type: "application/pdf",
          });
          uploadSignedDocument(pdfBlob);
          window.postMessage(
            {
              type: "CLOSE_POPUP",
            },
            "*",
          );
        } catch (err) {
          console.error("Failed to decode or parse the message:", err);
        }
      } else {
        console.log("Unexpected message format:", msg);
      }
    };

    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
    };
  }, []);

  useEffect(() => {
    if (open && appReview) {
      loadTemplate();
    }
  }, [open, appReview]);

  const handleClose = () => {
    setSaving(true);
    AppReviewRepository.getById(appReview.id)
      .then((res) => {
        setAppReview(res.data);
        setSaving(false);
        setOpen(false);
      })
      .catch((err) => {
        console.error(err);
        setSaving(false);
        dispatch(
          notifyShowErrorMessage(
            "Грешка при освежување на податоците за ревизијата.",
          ),
        );
      });
  };

  const loadTemplate = () => {
    AppReviewRepository.getInstitutionDecisionTemplate(
      appReview.id,
      appReview.isApproved,
    ).then((res) => {
      setSubject(res.data?.subject || "");
      setCaseDocuments(res.data?.caseDocuments || "");
      setDecision(res.data?.decision || "");
      setSummary(res.data?.summary || "");
      setRemarks(res.data?.remarks);
      setJpdpDirector(res.data?.jpdpDirector || "");
      setMvrMinister(res.data?.mvrMinister || "");
    });
  };

  const generateDecision = () => {
    setSaving(true);
    AppReviewRepository.generateInstitutionDecision(
      appReview.id,
      {
        subject: subject,
        caseDocuments: caseDocuments,
        decision: decision,
        remarks: remarks ? JSON.stringify(remarks) : null,
        summary: summary,
        jpdpDirector: jpdpDirector,
        mvrMinister: mvrMinister,
      },
      appReview?.isApproved,
    )
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        setGeneratedDocument(blob);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Decision_${appReview.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      })
      .catch((err) => {
        console.error("Error generating decision:", err);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // result looks like: data:application/pdf;base64,JVBERi0x...
        const base64 = reader.result.split(",")[1]; // remove metadata
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const uploadSignedDocument = (signedPdfBlob) => {
    setSaving(true);
    const file = new File([signedPdfBlob], "decision.pdf", {
      type: "application/pdf",
    });
    const formData = new FormData();
    formData.append("appReviewId", appReview.id);
    formData.append("decisionDocument", file);
    formData.append("approved", appReview.isApproved);
    AppReviewRepository.uploadSignedInstitutionDecision(formData)
      .then((res) => {
        dispatch(
          notifyShowSuccessMessage("Одлуката е успешно потпишана и испратена."),
        );
        handleClose();
      })
      .catch((err) => {
        setSaving(false);
        dispatch(
          notifyShowErrorMessage("Грешка при испраќање на потпишаната одлука."),
        );
        console.error("Error uploading signed document:", err);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const submitForSigning = () => {
    setSaving(true);
    const file = new File([generatedDocument], "decision.pdf", {
      type: "application/pdf",
    });
    const formData = new FormData();
    formData.append("appReviewId", appReview.id);
    formData.append("decisionDocument", file);
    formData.append("approved", appReview.isApproved);

    AppReviewRepository.submitForSignature(formData)
      .then((res) => {
        dispatch(
          notifyShowSuccessMessage("Одлуката е успешно поднесена за потпис."),
        );
        handleClose();
      })
      .catch((err) => {
        setSaving(false);
        dispatch(notifyShowErrorMessage("Грешка при поднесување на одлуката."));
        console.error("Error signing and uploading document:", err);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const moveItem = (arr, from, to) => {
    const copy = [...arr];
    const [item] = copy.splice(from, 1);
    copy.splice(to, 0, item);
    return copy;
  };

  // inside your component (where you have remarks, setRemarks)
  const addRemark = () => setRemarks([...(remarks ?? []), ""]);

  const addRemarkBelow = (index) => {
    const copy = [...(remarks ?? [])];
    copy.splice(index + 1, 0, "");
    setRemarks(copy);
  };

  const deleteRemark = (index) => {
    const copy = [...(remarks ?? [])];
    copy.splice(index, 1);
    setRemarks(copy);
  };

  const moveUp = (index) => {
    if (index === 0) return;
    setRemarks(moveItem(remarks, index, index - 1));
  };

  const moveDown = (index) => {
    if (!remarks || index >= remarks.length - 1) return;
    setRemarks(moveItem(remarks, index, index + 1));
  };

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="md">
        <DialogTitle>
          {appReview.isApproved
            ? "Генерирање на позитивна одлука"
            : "Генерирање на негативна одлука"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">
                <b>Предмет</b>
              </Typography>
              <ReactQuill
                theme="snow"
                value={subject}
                onChange={(value) => {
                  setSubject(value);
                }}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }], // headings
                    ["bold", "italic", "underline", "strike"], // text style
                    [{ list: "ordered" }, { list: "bullet" }], // lists
                    ["link", "image"], // links and images
                    [{ align: [] }],
                    ["clean"], // remove formatting
                  ],
                }}
              />
            </Grid>
            {appReview?.institutionType === "JPDP" && (
              <Grid item xs={12}>
                <Typography variant="subtitle1">
                  <b>Пристигнати прилози</b>
                </Typography>
                <ReactQuill
                  theme="snow"
                  value={caseDocuments}
                  onChange={(value) => {
                    setCaseDocuments(value);
                  }}
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }], // headings
                      ["bold", "italic", "underline", "strike"], // text style
                      [{ list: "ordered" }, { list: "bullet" }], // lists
                      ["link", "image"], // links and images
                      [{ align: [] }],
                      ["clean"], // remove formatting
                    ],
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle1">
                <b>Одлука</b>
              </Typography>
              <ReactQuill
                theme="snow"
                value={decision}
                onChange={(value) => {
                  setDecision(value);
                }}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }], // headings
                    ["bold", "italic", "underline", "strike"], // text style
                    [{ list: "ordered" }, { list: "bullet" }], // lists
                    ["link", "image"], // links and images
                    [{ align: [] }],
                    ["clean"], // remove formatting
                  ],
                }}
              />
            </Grid>

            {appReview?.institutionType === "MVR" &&
              appReview?.appEntry?.type === "TEMPORARY_TRAFFIC_CHANGE" &&
              appReview.isApproved === true && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    <b>Измени и дополненија</b>
                  </Typography>

                  <Table size="small" style={{ marginTop: "15px" }}>
                    {remarks &&
                      remarks?.map((item, index) => (
                        <TableRow key={index} style={{ borderBottom: "none" }}>
                          <TableCell style={{ border: "none" }}>
                            <TextField
                              fullWidth
                              size="small"
                              multiline
                              minRows={2}
                              sx={{
                                "& .MuiInputBase-input": { fontSize: "10px" },
                                "& .MuiInputLabel-root": { fontSize: "10px" },
                              }}
                              label={`Точка ${index + 1}`}
                              value={item}
                              onChange={(e) => {
                                const updated = [...remarks];
                                updated[index] = e.target.value;
                                setRemarks(updated);
                              }}
                            />
                          </TableCell>

                          <TableCell
                            width={120}
                            align="right"
                            style={{ border: "none" }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => moveUp(index)}
                              disabled={index === 0}
                              title="Горе"
                            >
                              <ArrowUpwardOutlined fontSize="inherit" />
                            </IconButton>

                            <IconButton
                              size="small"
                              onClick={() => moveDown(index)}
                              disabled={index === (remarks?.length ?? 0) - 1}
                              title="Долу"
                            >
                              <ArrowDownwardOutlined fontSize="inherit" />
                            </IconButton>

                            <IconButton
                              size="small"
                              onClick={() => addRemarkBelow(index)}
                              title="Додај подолу"
                            >
                              <AddCircle fontSize="inherit" />
                            </IconButton>

                            <IconButton
                              size="small"
                              onClick={() => deleteRemark(index)}
                              title="Избриши"
                            >
                              <DeleteOutline fontSize="inherit" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                  </Table>

                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    sx={{ mt: 1, mb: 1 }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddCircle />}
                      onClick={addRemark}
                    >
                      Додади точка најдолу
                    </Button>
                  </Stack>
                </Grid>
              )}
            <Grid item xs={12}>
              <Typography variant="subtitle1">
                <b>Резиме</b>
              </Typography>
              <ReactQuill
                theme="snow"
                value={summary}
                onChange={(value) => {
                  setSummary(value);
                }}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }], // headings
                    ["bold", "italic", "underline", "strike"], // text style
                    [{ list: "ordered" }, { list: "bullet" }], // lists
                    ["link", "image"], // links and images
                    [{ align: [] }],
                    ["clean"], // remove formatting
                  ],
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {generatedDocument ? (
            <Button
              variant="contained"
              color="success"
              onClick={() =>
                setOpenConfirmationDialog({
                  title: "Поднеси за потпис",
                  message: `Дали сте сигурни дека сакате да ја поднесете одлуката за потпис?`,
                  onConfirm: () => {
                    submitForSigning();
                    setOpenConfirmationDialog(null);
                  },
                })
              }
            >
              Поднеси за потпис
            </Button>
          ) : null}
          <Button
            variant={generatedDocument ? "outlined" : "contained"}
            onClick={() => {
              generateDecision();
            }}
          >
            {generatedDocument ? "Прегенирај одлука" : "Генерирај одлука"}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              setAppReview({
                ...appReview,
                isApproved: null,
              });
            }}
          >
            Откажи
          </Button>
        </DialogActions>
      </Dialog>

      {saving && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {openConfirmationDialog && (
        <ConfirmationDialog
          open={Boolean(openConfirmationDialog)}
          title={openConfirmationDialog.title}
          message={openConfirmationDialog.message}
          onCancel={() => setOpenConfirmationDialog()}
          onConfirm={openConfirmationDialog.onConfirm}
        />
      )}
    </>
  );
};
